import { components as coreComponents } from '@tempi/core-elements/dist/renderer';
import {
  BuilderComponent,
  BuilderConfig,
  Device,
  buildComponentDict,
} from '@tempi/core-renderer';
import { components as ecomComponents } from '@tempi/ecom-elements/dist/renderer';
import React, { useEffect, useState } from 'react';

import { useAuth } from '@teko/auth-react';
import { ScreenConfig } from '@terra-js/shop-front-kit';
import terraKit from '@terra-js/terra-kit';
import { getEnv } from 'helpers';
import {
  useLoyaltyMemberInfo,
  useTenantConfig,
  useTerraScreenConfig,
} from 'hooks';
import { useNetworkConfig } from 'hooks/useNetworkConfig';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { PbServerSideProps } from './getServerSideProps';
import { applyPageBackgroundColors } from './helper';
import { usePbPage } from './hooks';
import { mappingDynamicDesktopSlots, mappingDynamicMobileSlots } from './slots';
import {
  DynamicSlotComponents,
  ElementInterface,
  ExtraStoreState,
} from './types';

export type PbPageProps = PbServerSideProps & {
  page?: string;
};
const BackButtonHeaderMobile = dynamic(() =>
  import('./slots/BackButtonHeaderMobile').then(
    (f) => f.BackButtonHeaderMobile,
  ),
);

export const PbPage: React.FC<PbPageProps> = ({
  pbContent: serverContent,
  pbSSRData,
  menu: serverMenu,
  page = '/',
}: PbPageProps) => {
  const {
    isMobile,
    terminal,
    location,
    terminalId,
    epicOmni1632,
    currentLanguage,
  } = useTenantConfig();
  const device: Device = isMobile ? 'mobile' : 'desktop';
  const router = useRouter();
  const { query } = router;
  const { content, menu, websiteId } = usePbPage({
    page: page,
    device,
    defaultValue: {
      content: serverContent,
      menu: serverMenu || [],
    },
    languageCode: (query?.lang as string) || currentLanguage,
  });
  const { partner, getProcessEnv, isDisableLuckyDrawPhoneInput } =
    useTenantConfig();
  // @TODO: Will remove to replace with get backgroundColors from app Taka (epic OMNI-1632)
  const { networkConfig } = useNetworkConfig();
  const { loyaltyMemberInfo } = useLoyaltyMemberInfo();
  const backgroundColors = networkConfig?.networkTiers
    ?.find((item) => item.code === loyaltyMemberInfo?.currentTierCode)
    ?.tierTheme?.backgroundColors?.map((bg) => bg.value);

  const screenConfig: ScreenConfig | undefined = useTerraScreenConfig();
  const backappUrl = getProcessEnv('BACKAPP_URL');
  const { user } = useAuth();
  const [appUserPhone, setAppUserPhone] = useState<string>();

  const showBackButton =
    !screenConfig?.header?.isBackIconHidden && (!!partner || !!backappUrl);

  const mappingDynamicMobileSlotsAddNew: DynamicSlotComponents = {
    ...mappingDynamicMobileSlots,
    ...(showBackButton
      ? {
          back_button_header_mobile: BackButtonHeaderMobile,
        }
      : {}),
  };

  const mappingDynamicSlots =
    device === 'mobile'
      ? mappingDynamicMobileSlotsAddNew
      : mappingDynamicDesktopSlots;

  useEffect(() => {
    if (terraKit.isAvailable()) {
      terraKit
        .user()
        .getUserProfile()
        .then((appProfile) => {
          const userPhone = appProfile.value?.telephone;
          if (userPhone) {
            setAppUserPhone(userPhone);
          }
        });
    }
  }, []);

  return (
    <BuilderComponent<ElementInterface, ExtraStoreState>
      componentDict={buildComponentDict({
        ...ecomComponents,
        ...coreComponents,
      })}
      content={
        !!epicOmni1632 && backgroundColors
          ? applyPageBackgroundColors(content, backgroundColors)
          : content
      }
      ssrData={pbSSRData}
      configs={{
        device,
        env: getEnv('LANDING_BUILDER_ENV') as BuilderConfig['env'],
        globalData: {
          menu,
          userPhoneNumber:
            user?.telephone ||
            // in case webview in app consumer
            appUserPhone,
          isDisableLuckyDrawPhoneInput,
          websiteId,
          generateCustomUrl: (slug) =>
            `https://${window?.location?.hostname}/${slug}`,
        },
        terminalCode: terminal,
        terminalId: terminalId,
        locationCode: location,
        mappingDynamicSlots,
        featureFlags: { flagPB5049: true },
      }}
    />
  );
};
